import { apiAccessControl } from '@A/controllers/access-control.api'
import { useModulesService } from '@A/services/modules.service'
import { useAuthStore } from '@/stores/auth'
import { useModulesStore } from '@/stores/modules'
import { useOrganizationStore } from '@/stores/organization'
import { handleApiError } from '@/shared/utils/handleApiError'
import { noAssistantAccess } from '§§C/constants/ai-api-key'

/**
 * Access Control Service
 *
 * Handles fetching and initializing the current user's permissions and roles.
 * Used during application startup and when permissions need to be refreshed.
 */
export const useAccessControlService = () => {
  const { getCurrentUserPermissions, getCurrentUserRoles } = apiAccessControl()

  /**
   * Fetches current user's permissions and roles from the backend.
   * Updates the auth store with the fetched data.
   */
  const fetchCurrentUserPermissions = async (organizationId?: string): Promise<void> => {
    const authStore = useAuthStore()
    const organizationStore = useOrganizationStore()

    // Tag this fetch with a sequence number. When the response lands we drop it if
    // a newer fetch has been issued in the meantime (e.g. a rapid org switch, or
    // the router's org-scoped refresh racing the unscoped init). Without this, a
    // slow response can overwrite the auth store with stale data.
    const requestSeq = authStore.nextPermissionsRequestSeq()

    try {
      authStore.setPermissionsLoading(true)
      authStore.setPermissionsError(null)

      const [permissionsResponse, rolesResponse] = await Promise.all([
        getCurrentUserPermissions(organizationId),
        getCurrentUserRoles()
      ])

      if (!authStore.isLatestPermissionsRequest(requestSeq)) {
        return
      }

      if (permissionsResponse) {
        const permissions = {
          global: permissionsResponse.permissions || [],
          client: {},
          project: {},
          concept: {}
        }

        authStore.setPermissions(permissions)
        // access_tier is org-scoped: the backend only resolves it to something
        // meaningful when an organizationId is supplied, mirroring org permissions.
        // Admin access (isOrganizationAdmin) is derived from access_tier === 'admin'.
        authStore.setAccessTier(organizationId ? permissionsResponse.access_tier ?? 'none' : 'none')
        // Assistant access is org-scoped the same way; canUseAssistant reads it.
        authStore.setAssistantAccess(
          organizationId ? permissionsResponse.assistant ?? noAssistantAccess() : noAssistantAccess()
        )

        if (organizationId && permissionsResponse.organization_permissions) {
          authStore.setOrganizationPermissions(permissionsResponse.organization_permissions)
        }

        // Org list rides along with permissions, so the header switcher and picker
        // see new/revoked orgs on the next nav without a separate endpoint.
        organizationStore.setOrganizations(permissionsResponse.organizations || [])
      }

      // Load the organization's module subscriptions when an org context is supplied
      if (organizationId) {
        const { fetchOrganizationModules } = useModulesService()
        await fetchOrganizationModules(organizationId)
      }
      else {
        useModulesStore().clear()
      }

      if (rolesResponse) {
        authStore.setUserRoles(rolesResponse.roles || [])
      }
    } catch (error) {
      if (!authStore.isLatestPermissionsRequest(requestSeq)) {
        return
      }

      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch user permissions'
      authStore.setPermissionsError(errorMessage)
      handleApiError(error)

      // Set empty permissions on error so the app can still function
      authStore.setPermissions({
        global: [],
        client: {},
        project: {},
        concept: {}
      })
      authStore.setUserRoles([])
      authStore.setAccessTier('none')
      authStore.setAssistantAccess(noAssistantAccess())
    } finally {
      if (authStore.isLatestPermissionsRequest(requestSeq)) {
        authStore.setPermissionsLoading(false)
      }
    }
  }

  /**
   * Initializes current user permissions on app startup.
   * Only fetches if permissions haven't been loaded yet.
   */
  const initializeCurrentUserPermissions = async (): Promise<void> => {
    const authStore = useAuthStore()

    // Only fetch if not already loaded
    if (!authStore.permissionsLoaded) {
      await fetchCurrentUserPermissions()
    }
  }

  /**
   * Refreshes current user permissions.
   * Always fetches fresh data from the backend.
   */
  const refreshCurrentUserPermissions = async (): Promise<void> => {
    await fetchCurrentUserPermissions()
  }

  return {
    fetchCurrentUserPermissions,
    initializeCurrentUserPermissions,
    refreshCurrentUserPermissions
  }
}
